import { Coins, Gem, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { APIClient } from "./api";
import { assetURL } from "./assets";
import type { EquipmentItem } from "./types";
import { ResourceIcon } from "./ui/Resources";

export interface ShopView {
  period: string;
  refreshes: number;
  refreshCost: number;
  gold: number;
  diamond: number;
  crystal: number;
  goldCap: number;
  resetAt: number;
  offers: Array<{ item: EquipmentItem; gold: number; sold: boolean }>;
}
export function ShopScreen({ api, onChanged }: { api: APIClient; onChanged: () => Promise<void> }) {
  const [view, setView] = useState<ShopView>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [amount, setAmount] = useState("1");
  const [currency, setCurrency] = useState<"gold" | "crystal">("gold");
  const busyRef = useRef(false);
  const load = () => api.shop().then(setView);
  useEffect(() => {
    let active = true;
    const refresh = () => {
      if (document.hidden || busyRef.current) return;
      void api
        .shop()
        .then((v) => {
          if (active && !busyRef.current) setView(v);
        })
        .catch((e) => {
          if (active) setError(e.message);
        });
    };
    refresh();
    const timer = window.setInterval(refresh, 60000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [api]);
  const act = async (action: "buy" | "refresh" | "exchange" | "exchange_crystal", itemId = "") => {
    if (!view || busyRef.current) return;
    const cost =
      action === "buy"
        ? view.offers.find((o) => o.item.id === itemId)?.gold
        : action === "refresh"
          ? view.refreshCost
          : Number(amount);
    if (
      !window.confirm(
        action === "buy"
          ? `花费 ${cost} 金币购买这件装备？`
          : action === "refresh"
            ? `花费 ${cost} 钻石刷新全部商品？下一次费用翻倍。`
            : action === "exchange_crystal"
              ? `花费 ${cost} 钻石兑换 ${cost} 结晶？`
              : `花费 ${cost} 钻石兑换 ${Number(cost) * 100} 金币？`,
      )
    )
      return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      setView(await api.shop(action, `${view.period}:${view.refreshes}`, itemId, Number(amount)));
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "操作失败");
      await load().catch(() => undefined);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const valid =
    Number.isSafeInteger(Number(amount)) &&
    Number(amount) > 0 &&
    !!view &&
    Number(amount) <= view.diamond &&
    (currency === "crystal"
      ? Number(amount) <= Number.MAX_SAFE_INTEGER - view.crystal
      : Number(amount) * 100 <= view.goldCap - view.gold);
  return (
    <section className="shop-screen">
      <header className="hub-page-header">
        <h1>商店</h1>
        <span>
          <Coins size={14} />
          {view?.gold ?? 0}　<Gem size={14} />
          {view?.diamond ?? 0}
        </span>
      </header>
      {error && <p role="alert">{error}</p>}
      {!view ? (
        <button onClick={() => void load().catch((e) => setError(e.message))}>重新加载</button>
      ) : (
        <>
          <div className="shop-refresh">
            <small>每日 04:00 更新 · 今日刷新 {view.refreshes} 次</small>
            <button
              className="secondary-command"
              disabled={busy || view.refreshCost <= 0 || view.diamond < view.refreshCost}
              onClick={() => void act("refresh")}
            >
              <RefreshCw size={16} />
              <Gem size={14} />
              {view.refreshCost} 刷新
            </button>
          </div>
          <div className="shop-offers">
            {view.offers.map((o) => (
              <article key={o.item.id} className={`shop-offer quality-${o.item.quality}`}>
                <header>
                  <img
                    width={32}
                    height={32}
                    src={assetURL(`/assets/source/equipment/${o.item.tier}/${o.item.slot}.png`)}
                    alt=""
                  />
                  <strong>{o.item.name}</strong>
                </header>
                <small>
                  {o.item.tier.toUpperCase()} · 装等 {o.item.itemLevel} ·{" "}
                  {o.item.quality === "epic" ? "秘银" : o.item.quality === "rare" ? "符刻" : "精制"}{" "}
                  · 需求 Lv.{o.item.requirements?.level ?? 1}
                </small>
                <div>
                  攻击 {o.item.baseAttack} · 防御 {o.item.baseDefense} · 生命 {o.item.baseHp}
                </div>
                <ul>
                  {[...(o.item.implicitAffixes ?? []), ...(o.item.affixes ?? [])].map((a, i) => (
                    <li key={i}>
                      {a.name}{" "}
                      {a.mode === "percent"
                        ? `${Math.round(a.value * 100)}%`
                        : a.mode === "mechanic"
                          ? a.description
                          : a.value}
                    </li>
                  ))}
                </ul>
                <button
                  className="primary-command"
                  disabled={busy || o.sold || view.gold < o.gold}
                  onClick={() => void act("buy", o.item.id)}
                >
                  <Coins size={15} />
                  {o.sold ? "已售出" : o.gold.toLocaleString()}
                </button>
              </article>
            ))}
          </div>
          <form
            className="shop-exchange"
            onSubmit={(e) => {
              e.preventDefault();
              void act(currency === "crystal" ? "exchange_crystal" : "exchange");
            }}
          >
            <label>
              钻石兑换
              <select
                aria-label="兑换资源"
                value={currency}
                onChange={(e) => setCurrency(e.target.value as "gold" | "crystal")}
              >
                <option value="gold">金币 · 1 : 100</option>
                <option value="crystal">结晶 · 1 : 1</option>
              </select>
              <input
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </label>
            <span>
              <ResourceIcon kind={currency} size={15} />
              {valid ? Number(amount) * (currency === "crystal" ? 1 : 100) : 0}{" "}
              {currency === "crystal" ? "结晶" : "金币"}
            </span>
            <button className="secondary-command" disabled={busy || !valid}>
              <Gem size={15} />
              兑换
            </button>
            <small>
              {currency === "crystal"
                ? `结晶余额 ${view.crystal}`
                : `金币容量 ${view.gold}/${view.goldCap}`}
            </small>
          </form>
        </>
      )}
    </section>
  );
}
